// date-picker-flyout.component.ts
import { ChangeDetectorRef, Component, ElementRef, EventEmitter, forwardRef, HostBinding, HostListener, Inject, Input, LOCALE_ID, Output, ViewChild } from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatCalendar } from '@angular/material/datepicker';
import { calculateFlyoutPosition, DateLocalePipe, repositionFlyoutAfterRender } from '@modeso/aumico-lib-core-fe';
import { I18NextService } from 'angular-i18next';
import moment, { Moment } from 'moment';

@Component({
  selector: 'app-date-picker-flyout',
  templateUrl: './date-picker-flyout.component.html',
  styleUrls: ['./date-picker-flyout.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DatePickerFlyoutComponent),
      multi: true
    }
  ]
})
export class DatePickerFlyoutComponent implements ControlValueAccessor {
  @Input() formControl: FormControl;
  @Input() label: string;
  @Input() required = false;
  @Input() disabled = false;
  @Input() showError = false;
  @Input() errorMessage: string;
  @Input() flyoutClass: string;
  /** Fill the parent's width instead of the fixed 350 px — e.g. two pickers side by side in a modal. */
  @Input() @HostBinding('class.is-full-width') fullWidth = false;

  @Output() dateSelected = new EventEmitter<Date>();

  @ViewChild('dateButton') dateButton: any;
  @ViewChild('dateInput') dateInput: ElementRef<HTMLInputElement>;
  @ViewChild(MatCalendar) calendar: MatCalendar<Moment>;
  @ViewChild('calendarFlyout') calendarFlyout: ElementRef<HTMLElement>;

  isOpen = false;
  isEditingText = false;
  selectedDate: Moment | null = null; // Use Moment type for mat-calendar
  flyoutPosition: {
    top: number;
    left: number;
    right: number;
    isAbove?: boolean;
  } = { top: 0, left: 0, right: 0, isAbove: false };

  // ControlValueAccessor implementation
  private value: Date | null = null;
  private onChange = (value: Date | null) => { };
  private onTouched = () => { };

  constructor(
    private elementRef: ElementRef<HTMLElement>,
    private dateLocalePipe: DateLocalePipe,
    private cdr: ChangeDetectorRef,
    private i18NextService: I18NextService,
    @Inject(LOCALE_ID) private locale: string
  ) { }

  get formattedDate(): string {
    // Use internal value if available, otherwise fall back to formControl for backward compatibility
    const dateToFormat = this.value || (this.formControl ? this.formControl.value : null);
    if (!dateToFormat) return '';
    return this.dateLocalePipe.transform(dateToFormat, undefined, undefined, false, false).toString();
  }

  get dateFormatPlaceholder(): string {
    const language = this.i18NextService.language;
    const formatter = new Intl.DateTimeFormat(language);
    const parts = formatter.formatToParts(new Date(2000, 0, 15));
    const map = {
      day: 'dd',
      month: 'mm',
      year: 'yyyy'
    };

    return parts.map(part => part.type === 'literal' ? part.value : map[part.type] || part.value).join('');
  }

  openFlyout(buttonWrapper: any): void {
    if (this.disabled) return;

    // Convert Date to Moment for mat-calendar - use internal value or formControl
    const dateValue = this.value || (this.formControl ? this.formControl.value : null);
    this.selectedDate = dateValue ? moment.utc(dateValue).startOf('day') : null;

    this.isOpen = true;
    this.isEditingText = false;
    this.positionFlyout(buttonWrapper);

    // Update calendar after it's rendered
    setTimeout(() => {
      if (this.calendar && this.selectedDate) {
        this.calendar.activeDate = moment.utc(this.selectedDate).startOf('day');
        this.cdr.detectChanges();
      }
    });
  }

  closeFlyout(): void {
    this.isOpen = false;
  }

  /**
   * A click anywhere outside the calendar closes it. The flyout's backdrop does that only outside a
   * `ham-modal` — inside one the modal lies above the backdrop. Read from the event's path, not its
   * target: switching the calendar to the month or year view removes the clicked cell before the
   * click arrives here.
   */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.isOpen) return;
    const path = event.composedPath();
    const onCalendar = this.calendarFlyout != null && path.includes(this.calendarFlyout.nativeElement);
    if (!onCalendar && !path.includes(this.elementRef.nativeElement)) {
      this.closeFlyout();
    }
  }

  onDateChange(date: Moment): void {
    // Build UTC midnight for that calendar day
    const year = date.year();
    const month = date.month(); // 0-based
    const dday = date.date();

    const asUtc = new Date(Date.UTC(year, month, dday, 0, 0, 0, 0));
    this.selectedDate = moment.utc(asUtc).startOf('day');

    // Update internal value and notify Angular forms
    this.value = asUtc;
    this.onChange(asUtc);
    this.onTouched();

    // Update FormControl if provided (backward compatibility)
    if (this.formControl) {
      this.formControl.patchValue(asUtc);
      this.formControl.markAsTouched();
    }

    this.dateSelected.emit(asUtc);
    this.closeFlyout();
  }

  selectToday(): void {
    const today = moment();
    this.onDateChange(today);
  }

  clearDate(): void {
    this.selectedDate = null;

    // Update internal value and notify Angular forms
    this.value = null;
    this.onChange(null);
    this.onTouched();

    // Update FormControl if provided (backward compatibility)
    if (this.formControl) {
      this.formControl.patchValue(null);
      this.formControl.markAsTouched();
    }

    this.dateSelected.emit(null);
    this.closeFlyout();
  }

  onLabelClick(event: Event): void {
    if (this.disabled) return;
    event.stopPropagation();
    this.isEditingText = true;
    this.isOpen = false;

    // Focus the input after render
    setTimeout(() => {
      if (this.dateInput) {
        this.dateInput.nativeElement.focus();
        this.dateInput.nativeElement.select();
      }
    }, 0);
  }

  onTextInputBlur(): void {
    this.isEditingText = false;
    this.cdr.detectChanges();
  }

  onTextInputKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.isEditingText = false;
      event.preventDefault();
      this.cdr.detectChanges();
    } else if (event.key === 'Enter') {
      this.parseAndSetDate((event.target as HTMLInputElement).value);
      this.isEditingText = false;
      event.preventDefault();
    }
  }

  onTextInputChange(value: string): void {
    // Don't parse on every keystroke, only on blur or Enter
    // This prevents invalid intermediate states
  }

  onTextInputBlurWithParse(): void {
    if (this.dateInput) {
      this.parseAndSetDate(this.dateInput.nativeElement.value);
    }
    this.isEditingText = false;
    this.cdr.detectChanges();
  }

  private parseAndSetDate(value: string): void {
    if (!value || value.trim() === '') {
      this.clearDate();
      return;
    }

    // Try to parse the date using moment
    const date = this.parseDate(value);

    if (date && date.isValid()) {
      // Create normalized Date with only year, month, day (time set to 00:00:00)
      const jsDate = new Date(Date.UTC(date.year(), date.month(), date.date(), 0, 0, 0, 0));
      this.selectedDate = moment.utc(jsDate).startOf('day');

      // Update internal value and notify Angular forms
      this.value = jsDate;
      this.onChange(jsDate);
      this.onTouched();

      // Update FormControl if provided (backward compatibility)
      if (this.formControl) {
        this.formControl.patchValue(jsDate);
        this.formControl.markAsTouched();
      }

      this.dateSelected.emit(jsDate);
      this.cdr.detectChanges();
    } else {
      // Invalid date - mark as touched
      this.onTouched();
      if (this.formControl) {
        this.formControl.markAsTouched();
      }
      this.cdr.detectChanges();
    }
  }

  private parseDate(value: string): moment.Moment | null {
    // Remove any extra spaces
    value = value.trim();

    // Get the current locale's date formats
    const localeData = moment.localeData();
    const formats = [
      localeData.longDateFormat('L'),    // Short date format (e.g., MM/DD/YYYY or DD.MM.YYYY)
      localeData.longDateFormat('l'),    // Short date format without leading zeros
      'YYYY-MM-DD',                       // ISO format
      'DD.MM.YYYY',                       // Common European
      'DD/MM/YYYY',                       // European with slash
      'DD-MM-YYYY',                       // European with dash
      'MM/DD/YYYY',                       // US format
      'MM-DD-YYYY',                       // US with dash
      'DD.MM.YY',                         // Short year European
      'DD/MM/YY',                         // Short year European slash
      'MM/DD/YY',                         // Short year US
      'YYYY/MM/DD',                       // ISO with slash
    ];

    // Try to parse with each format - use UTC to avoid timezone issues
    const parsedDate = moment.utc(value, formats, true); // true = strict parsing

    if (parsedDate.isValid()) {
      // Create a local moment with only year, month, day (ignoring timezone)
      return moment([parsedDate.year(), parsedDate.month(), parsedDate.date()]);
    }

    // If strict parsing fails, try lenient parsing as last resort
    const lenientDate = moment.utc(value);

    if (lenientDate.isValid()) {
      // Create a local moment with only year, month, day (ignoring timezone)
      return moment([lenientDate.year(), lenientDate.month(), lenientDate.date()]);
    }

    return null;
  }

  private positionFlyout(buttonWrapper: any): void {
    const buttonElement = buttonWrapper.nativeButton?.nativeElement;
    if (!buttonElement) return;

    const rect = buttonElement.getBoundingClientRect();
    const position = calculateFlyoutPosition(rect, undefined, 224, 446);

    this.flyoutPosition = {
      top: position.top,
      right: position.right,
      left: position.left,
      isAbove: position.isAbove,
    };

    // Reposition after render with actual dimensions
    setTimeout(() => {
      this.flyoutPosition = repositionFlyoutAfterRender(`.${this.flyoutClass}`, rect);
      this.cdr.detectChanges();
    }, 0);
  }

  // ControlValueAccessor implementation
  writeValue(value: Date | null): void {
    this.value = value;
    this.selectedDate = value ? moment.utc(value).startOf('day') : null;
    this.cdr.markForCheck();
  }

  registerOnChange(fn: (value: Date | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.cdr.markForCheck();
  }
}